<script>
  import { onMount } from 'svelte';
  import WhatsAppButton from './WhatsAppButton.svelte';
  import { t } from '../../services/i18n.js';
  import { trackItemView } from '../../services/analyticsService.js';

  /**
   * @type {{ 
   *   item: import('../../services/inventoryService.js').InventoryItem, 
   *   onClose: () => void 
   * }}
   */
  let { item, onClose } = $props();

  let currentImageIndex = $state(0);
  let imageLoadingStates = $state({});
  let imageCache = $state({});

  // All images including main image
  let allImages = $derived(() => {
    const images = [item.imageUrl, ...(item.images || [])].filter(Boolean);
    return images.length > 0 ? images : [];
  });

  // Check if current image is loading
  let isCurrentImageLoading = $derived(imageLoadingStates[currentImageIndex] ?? true);

  /**
   * Preload an image and update its loading state
   * @param {string} src - Image URL
   * @param {number} index - Image index
   */
  function preloadImage(src, index) {
    // Skip if already cached or loading
    if (imageCache[index] || imageLoadingStates[index] === false) {
      return;
    }

    imageLoadingStates[index] = true;

    const img = new Image();
    img.onload = () => {
      imageCache[index] = true;
      imageLoadingStates[index] = false;
    };
    img.onerror = () => {
      imageLoadingStates[index] = false;
    };
    img.src = src;
  }

  /**
   * Preload current and adjacent images
   */
  function preloadAdjacentImages() {
    const images = allImages();
    if (images.length === 0) return;

    // Preload current image
    preloadImage(images[currentImageIndex], currentImageIndex);

    // Preload next and previous images
    const nextIndex = (currentImageIndex + 1) % images.length;
    const prevIndex = currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;

    preloadImage(images[nextIndex], nextIndex);
    preloadImage(images[prevIndex], prevIndex);
  }

  /**
   * Format price in Brazilian Real
   * @param {number} price
   */
  function formatPrice(price) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }

  function nextImage() {
    const images = allImages();
    if (images.length > 1) {
      currentImageIndex = (currentImageIndex + 1) % images.length;
      preloadAdjacentImages();
    }
  }

  function prevImage() {
    const images = allImages();
    if (images.length > 1) {
      currentImageIndex = currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;
      preloadAdjacentImages();
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowRight') {
      nextImage();
    } else if (e.key === 'ArrowLeft') {
      prevImage();
    }
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  onMount(() => {
    // Track item view
    trackItemView(item);

    // Preload all images on mount
    const images = allImages();
    images.forEach((src, index) => {
      preloadImage(src, index);
    });

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    // Add keyboard listener
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  });
</script>

<div 
  class="modal-backdrop" 
  role="dialog" 
  aria-modal="true"
  aria-labelledby="modal-title"
  tabindex="-1"
  onclick={handleBackdropClick}
  onkeydown={handleKeyDown}
>
  <div class="modal-container">
    <button 
      class="modal-close" 
      onclick={onClose}
      aria-label={$t('item.close')}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>

    <div class="modal-content">
      <!-- Image Gallery -->
      <div class="modal-gallery">
        {#if allImages().length > 0}
          <div class="gallery-main">
            <!-- Loading Spinner -->
            {#if isCurrentImageLoading}
              <div class="gallery-loading">
                <div class="spinner"></div>
              </div>
            {/if}
            
            <img 
              src={allImages()[currentImageIndex]} 
              alt={`${item.name} - Imagem ${currentImageIndex + 1}`}
              class="gallery-image"
              class:loading={isCurrentImageLoading}
            />
            
            {#if allImages().length > 1}
              <button class="gallery-nav prev" onclick={prevImage} aria-label="Imagem anterior">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>
              <button class="gallery-nav next" onclick={nextImage} aria-label="Próxima imagem">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
              
              <div class="gallery-dots">
                {#each allImages() as _, index}
                  <button 
                    class="gallery-dot"
                    class:active={currentImageIndex === index}
                    onclick={() => { currentImageIndex = index; preloadAdjacentImages(); }}
                    aria-label={`Ir para imagem ${index + 1}`}
                  ></button>
                {/each}
              </div>
            {/if}
          </div>
        {:else}
          <div class="gallery-placeholder">
            <span>📷</span>
            <p>Sem imagem</p>
          </div>
        {/if}
      </div>

      <!-- Item Details -->
      <div class="modal-details">
        <h2 id="modal-title" class="item-title">
          {#if item.brand && item.brand.trim() !== '' && item.brand.toLowerCase() !== 'não identificada'}
            {item.name} - {item.brand}
          {:else}
            {item.name}
          {/if}
        </h2>
        
        <div class="item-price-large">
          {formatPrice(item.price)}
        </div>

        <div class="item-attributes">
          {#if item.size}
            <div class="attribute">
              <span class="attribute-label">{$t('item.size')}:</span>
              <span class="attribute-value">{item.size}</span>
            </div>
          {/if}
          
          <div class="attribute">
            <span class="attribute-label">{$t('item.category')}:</span>
            <span class="attribute-value">{item.category}</span>
          </div>
          
          <div class="attribute">
            <span class="attribute-label">{$t('item.condition')}:</span>
            <span class="attribute-value">{item.condition}</span>
          </div>
        </div>

        {#if item.description}
          <div class="item-description">
            <h3>{$t('item.description')}</h3>
            <p>{item.description}</p>
          </div>
        {/if}

        <div class="item-actions">
          <WhatsAppButton {item} />
        </div>
      </div>
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-md);
    z-index: var(--z-modal-backdrop);
    animation: fadeIn var(--transition-fast);
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .modal-container {
    position: relative;
    background-color: var(--color-surface);
    border-radius: var(--border-radius-lg);
    max-width: 900px;
    max-height: 90vh;
    width: 100%;
    overflow: hidden;
    box-shadow: var(--shadow-xl);
    animation: slideUp var(--transition-normal);
  }

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-close {
    position: absolute;
    top: var(--spacing-md);
    right: var(--spacing-md);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--color-background);
    border-radius: var(--border-radius-full);
    color: var(--color-text);
    z-index: 10;
    transition: all var(--transition-fast);
  }

  .modal-close:hover {
    background-color: var(--color-error);
    color: white;
  }

  .modal-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    max-height: 90vh;
    overflow-y: auto;
  }

  .modal-gallery {
    background-color: var(--color-background);
    aspect-ratio: 1;
  }

  .gallery-main {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .gallery-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity var(--transition-fast);
  }

  .gallery-image.loading {
    opacity: 0.3;
  }

  .gallery-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.3);
    z-index: 5;
  }

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(255, 255, 255, 0.2);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .gallery-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted);
    font-size: 4rem;
  }

  .gallery-placeholder p {
    font-size: 1rem;
    margin-top: var(--spacing-sm);
  }

  .gallery-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: var(--border-radius-full);
    color: var(--color-text);
    transition: all var(--transition-fast);
  }

  .gallery-nav:hover {
    background-color: var(--color-primary);
    color: white;
  }

  .gallery-nav.prev {
    left: var(--spacing-md);
  }

  .gallery-nav.next {
    right: var(--spacing-md);
  }

  .gallery-dots {
    position: absolute;
    bottom: var(--spacing-md);
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: var(--spacing-xs);
  }

  .gallery-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.5);
    transition: all var(--transition-fast);
  }

  .gallery-dot:hover,
  .gallery-dot.active {
    background-color: white;
  }

  .modal-details {
    padding: var(--spacing-xl);
    overflow-y: auto;
  }

  .item-title {
    font-size: var(--font-size-2xl);
    color: var(--color-text);
    margin-bottom: var(--spacing-md);
  }

  .item-price-large {
    font-size: var(--font-size-3xl);
    font-weight: 700;
    color: var(--color-primary);
    margin-bottom: var(--spacing-lg);
  }

  .item-attributes {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-md);
    margin-bottom: var(--spacing-lg);
    padding: var(--spacing-md);
    background-color: var(--color-background);
    border-radius: var(--border-radius);
  }

  .attribute {
    display: flex;
    gap: var(--spacing-xs);
  }

  .attribute-label {
    color: var(--color-text-secondary);
    font-size: var(--font-size-sm);
  }

  .attribute-value {
    font-weight: 500;
    font-size: var(--font-size-sm);
  }

  .item-description {
    margin-bottom: var(--spacing-lg);
  }

  .item-description h3 {
    font-size: var(--font-size-base);
    color: var(--color-text-secondary);
    margin-bottom: var(--spacing-sm);
  }

  .item-description p {
    color: var(--color-text);
    line-height: var(--line-height-relaxed);
  }

  .item-actions {
    margin-top: auto;
  }

  @media (max-width: 768px) {
    .modal-container {
      max-height: 95vh;
    }

    .modal-content {
      grid-template-columns: 1fr;
    }

    .modal-gallery {
      aspect-ratio: 4/3;
    }

    .modal-details {
      padding: var(--spacing-lg);
    }

    .item-title {
      font-size: var(--font-size-xl);
    }

    .item-price-large {
      font-size: var(--font-size-2xl);
    }
  }
</style>
